import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, deDE, GridColDef } from '@mui/x-data-grid';

import {
  ArnoFieldState,
  IArnoFieldUsage,
  IArnoSimulation,
  useArnoFieldUsage,
  useArnoFieldUsagePareto,
  useArnoReadyMappingBatch,
  useSimulateArnoFieldMappings,
} from '../../hooks/reactQuerys/ArnoAdminQueries';
import {
  EVIDENCE_LABEL,
  paretoSentence,
  stateChip,
  STATE_LABEL,
} from './fieldEvidence';
import { FieldEvidenceDrawer } from './FieldEvidenceDrawer';

/**
 * Legacy-Felder nach Hebelwirkung (#1245).
 *
 * Die Liste beantwortet eine einzige Frage: *welches* Feld zuzuordnen bringt am
 * meisten. Sortiert wird deshalb nach blockierten Word-Bedingungen und nicht
 * alphabetisch — 2 792 Felder alphabetisch durchzugehen wäre Beschäftigung
 * statt Arbeit.
 */

const STATE_OPTIONS: ArnoFieldState[] = [
  'READY_TO_CONFIRM',
  'NEEDS_EVIDENCE',
  'NO_DINO_SOURCE',
  'CONFIRMED',
];

export const FieldImpactPanel: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [state, setState] = useState<ArnoFieldState | ''>('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState<string | null>(null);
  const [simulation, setSimulation] = useState<IArnoSimulation | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { fieldUsagePage, isFetchingFieldUsage } = useArnoFieldUsage({
    page,
    pageSize,
    search: search || undefined,
    state: state || undefined,
    onlyBlocking: true,
  });
  const { pareto } = useArnoFieldUsagePareto();
  const { readyBatch } = useArnoReadyMappingBatch();
  const { simulateFieldMappings, isSimulatingFieldMappings } =
    useSimulateArnoFieldMappings();

  const rows = useMemo(() => fieldUsagePage?.rows ?? [], [fieldUsagePage]);
  const summary = fieldUsagePage?.summary;

  const simulateReady = async () => {
    const keys = (readyBatch?.rows ?? []).map(row => row.sourceKey);
    if (keys.length === 0) {
      return;
    }
    setSimulation(await simulateFieldMappings(keys));
  };

  const columns: GridColDef[] = [
    {
      field: 'sourceKey',
      headerName: 'Legacy-Feld',
      flex: 2,
      minWidth: 200,
      renderCell: parameters => {
        const row = parameters.row as IArnoFieldUsage;
        return (
          <Box>
            <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
              {row.sourceKey}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {row.family ? `Familie ${row.family} · ` : ''}
              {row.occurrences} Vorkommen
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'blockedIfs',
      headerName: 'Blockierte Bedingungen',
      width: 180,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'blockedReachableIfs',
      headerName: 'davon im Klon erreichbar',
      description:
        'Eine Bedingung wird nur übersetzt, wenn auch jede äußere Bedingung über ihr übersetzt wird.',
      width: 200,
      align: 'right',
      headerAlign: 'right',
      renderCell: parameters => {
        const row = parameters.row as IArnoFieldUsage;
        const reach = row.blockedReachableIfs ?? 0;
        return (
          <Typography
            variant='body2'
            color={reach === 0 ? 'text.disabled' : 'text.primary'}
          >
            {reach}
          </Typography>
        );
      },
    },
    {
      field: 'blockCount',
      headerName: 'Bausteine',
      width: 110,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'templateCount',
      headerName: 'Rechtsgeschäftsarten',
      width: 170,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'state',
      headerName: 'Stand',
      width: 210,
      renderCell: parameters => {
        const row = parameters.row as IArnoFieldUsage;
        const chip = stateChip(row.state);
        return <Chip size='small' color={chip.color} label={chip.label} />;
      },
    },
    {
      field: 'evidenceStatus',
      headerName: 'Beweislage',
      width: 160,
      valueGetter: parameters =>
        EVIDENCE_LABEL[(parameters.row as IArnoFieldUsage).evidenceStatus],
    },
    {
      field: 'candidate',
      headerName: 'DiNo-Kandidat',
      flex: 2,
      minWidth: 220,
      renderCell: parameters => {
        const row = parameters.row as IArnoFieldUsage;
        return (
          <Typography variant='caption' color='text.secondary'>
            {row.targetPath ?? row.candidate ?? '–'}
          </Typography>
        );
      },
    },
  ];

  return (
    <Box>
      {summary && (
        <Alert severity='info' sx={{ mb: 2 }}>
          <AlertTitle>
            {summary.blockedIfs} Word-Bedingungen warten auf{' '}
            {summary.blockingFields} Feldzuordnungen
          </AlertTitle>
          <Typography variant='body2'>
            {paretoSentence(
              pareto?.marks?.p50?.fields,
              pareto?.marks?.p50?.blockedIfs,
              50
            )}{' '}
            {paretoSentence(
              pareto?.marks?.p80?.fields,
              pareto?.marks?.p80?.blockedIfs,
              80
            )}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {summary.safeIfs} Bedingungen sind sicher übernehmbar, davon{' '}
            {summary.safeReachableIfs ?? 0} vom Klon heute erreichbar · von den{' '}
            {summary.blockedIfs} blockierten erreicht der Klon{' '}
            {summary.blockedReachableIfs ?? 0} · gemessen am{' '}
            {summary.analysedAt ?? 'unbekannt'}
          </Typography>
        </Alert>
      )}

      {readyBatch && readyBatch.rows.length > 0 && (
        <Alert severity='success' sx={{ mb: 2 }}>
          <AlertTitle>
            {readyBatch.rows.length} bestätigungsreife Zuordnungen
          </AlertTitle>
          <Typography variant='body2'>
            {readyBatch.rows
              .map(row => `${row.sourceKey} → ${row.candidate}`)
              .join(' · ')}
          </Typography>
          <Stack direction='row' spacing={1} sx={{ mt: 1 }} alignItems='center'>
            <Button
              size='small'
              variant='outlined'
              disabled={isSimulatingFieldMappings}
              onClick={simulateReady}
            >
              Wirkung rechnen
            </Button>
            {simulation && (
              <Typography variant='body2'>
                {simulation.gainedIfs} Bedingungen würden sicher konvertierbar:{' '}
                {simulation.safeBefore} → {simulation.safeAfter}; offen{' '}
                {simulation.blockedBefore} → {simulation.blockedAfter}.
              </Typography>
            )}
          </Stack>
          <Typography variant='caption' color='text.secondary'>
            Bestätigt wird jede Zeile einzeln — die Rechnung ändert nichts.
          </Typography>
        </Alert>
      )}

      <Stack
        direction='row'
        spacing={2}
        sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
      >
        <TextField
          size='small'
          label='Legacy-Feld suchen'
          value={searchInput}
          onChange={event => setSearchInput(event.target.value)}
          sx={{ minWidth: 240 }}
        />
        <TextField
          size='small'
          select
          label='Stand'
          value={state}
          onChange={event => {
            setState(event.target.value as ArnoFieldState | '');
            setPage(0);
          }}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value=''>Alle</MenuItem>
          {STATE_OPTIONS.map(option => (
            <MenuItem key={option} value={option}>
              {STATE_LABEL[option]}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <DataGrid
        autoHeight
        localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
        rows={rows}
        columns={columns}
        getRowId={row => row.idLegacyFieldUsage}
        loading={isFetchingFieldUsage}
        paginationMode='server'
        rowCount={fieldUsagePage?.total ?? 0}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={model => {
          setPage(model.page);
          setPageSize(model.pageSize);
        }}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        onRowClick={parameters =>
          setSelected((parameters.row as IArnoFieldUsage).sourceKey)
        }
        sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
      />

      <Typography variant='caption' color='text.secondary' sx={{ mt: 1 }}>
        Eine Zeile anklicken zeigt, wie das Feld tatsächlich verwendet wird.
        Ohne belegte Bedeutung wird nichts zugeordnet — auch dann nicht, wenn
        der Name naheliegt.
      </Typography>

      <FieldEvidenceDrawer
        sourceKey={selected}
        onClose={() => setSelected(null)}
      />
    </Box>
  );
};
